/**
 * FoodBusinessPage — single business profile (restaurant, cafe,
 * supermarket, food truck, farm, food bank, distributor…).
 *
 * Route: /food-pulse/businesses/:slug
 * Backend: GET /api/food-businesses/:slug
 */
import { useEffect, useState } from "react";
import { Link, useParams, useLocation } from "react-router-dom";
import EntityMessageBoard from "@/components/EntityMessageBoard";
import TalkVibesTab from "@/components/TalkVibesTab";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, MapPin, Phone, Globe, Mail, Clock, BadgeCheck,
  Utensils, Sparkles, Store, Heart, ExternalLink, X, Loader2,
  Instagram, Facebook, Youtube, Award, Newspaper, Star, DollarSign, Sprout,
} from "lucide-react";
import SeoHead from "@/components/SeoHead";
import ShareButton from "@/components/ShareButton";
import { CharityImpactBadges, CharityContactForm } from "@/components/foodpulse/CharityBlocks";
import SplinkQrCard from "@/components/SplinkQrCard";
import { categoryIllustration } from "@/config/categoryIllustrations";

const API = process.env.REACT_APP_BACKEND_URL;

function prettyType(t) {
  if (!t) return "Food business";
  return t.replace(/_/g, " ").replace(/\b\w/g, (m) => m.toUpperCase());
}

function AmenityRow({ amenities }) {
  if (!amenities) return null;
  const rows = [
    ["dine_in",       "Dine-in"],
    ["takeaway",      "Takeaway"],
    ["delivery",      "Delivery"],
    ["drive_through", "Drive-through"],
    ["outdoor",       "Outdoor seating"],
    ["wheelchair",    "Wheelchair accessible"],
  ].filter(([k]) => amenities[k]);
  if (rows.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-2">
      {rows.map(([k, label]) => (
        <span
          key={k}
          className="text-[11px] font-mono px-2 py-0.5 rounded-full"
          style={{ background: "#F0E2C7", color: "#8B5E10" }}
          data-testid={`amenity-${k}`}
        >
          {label}
        </span>
      ))}
    </div>
  );
}

function ClaimModal({ business, onClose, onSubmitted }) {
  const [step, setStep] = useState("form"); // form | otp | proof | done
  const [form, setForm] = useState({
    claimant_name: "", claimant_role: "owner",
    claimant_email: "", claimant_phone: "",
    message: "",
    correction_phone: business?.phone || "",
    correction_website: business?.website || "",
    correction_hours: business?.hours || "",
    correction_email: business?.email || "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [claimId, setClaimId] = useState(null);
  const [otpDev, setOtpDev] = useState(null);      // set only in dev mode
  const [emailStatus, setEmailStatus] = useState(null);
  const [code, setCode] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(0);

  const [manageUrl, setManageUrl] = useState(null);      // /business/:id/edit after verify
  const [businessId, setBusinessId] = useState(null);

  // Proof uploads (step="proof")
  const [proofs, setProofs] = useState([]);              // uploaded so far
  const [uploadingKind, setUploadingKind] = useState(""); // active upload
  const MAX_PROOFS = 5;
  const PROOF_KINDS = [
    { key: "license",         label: "Business license",             hint: "Official state or county trading license" },
    { key: "utility_bill",    label: "Utility bill",                 hint: "Recent bill in the business name at the listing address" },
    { key: "receipt",         label: "Letterheaded receipt",         hint: "Any receipt printed on your letterhead" },
    { key: "chamber_letter",  label: "Chamber / association letter", hint: "Letter from local Chamber of Commerce or a trade association" },
    { key: "photo",           label: "Storefront / interior photo",  hint: "Photo showing your signage or interior in operation" },
    { key: "other",           label: "Other document",               hint: "Anything else that proves your ownership" },
  ];

  const upd = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  // Countdown for resend cooldown (45s server-side).
  useEffect(() => {
    if (resendCountdown <= 0) return;
    const t = setTimeout(() => setResendCountdown((n) => n - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCountdown]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.claimant_name.trim() || !form.claimant_email.trim()) {
      toast.error("Name and email are required.");
      return;
    }
    setSubmitting(true);
    try {
      const corrections = {};
      if (form.correction_phone   !== (business?.phone   || "")) corrections.phone   = form.correction_phone   || null;
      if (form.correction_website !== (business?.website || "")) corrections.website = form.correction_website || null;
      if (form.correction_hours   !== (business?.hours   || "")) corrections.hours   = form.correction_hours   || null;
      if (form.correction_email   !== (business?.email   || "")) corrections.email   = form.correction_email   || null;

      const res = await axios.post(`${API}/api/food-businesses/${business.slug}/claim`, {
        business_slug: business.slug,
        claimant_name: form.claimant_name.trim(),
        claimant_role: form.claimant_role,
        claimant_email: form.claimant_email.trim(),
        claimant_phone: form.claimant_phone.trim() || null,
        message: form.message.trim() || null,
        corrections: Object.keys(corrections).length ? corrections : null,
      });
      setClaimId(res.data?.claim_id || null);
      setOtpDev(res.data?.otp_dev || null);
      setEmailStatus(res.data?.email_status || null);
      setResendCountdown(45);
      setStep("otp");
      toast.success(
        res.data?.email_status === "sent"
          ? "Code sent — check your email!"
          : "Code generated — enter it below to verify."
      );
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const verify = async (e) => {
    e.preventDefault();
    if (!/^\d{4,8}$/.test(code.trim())) {
      toast.error("Enter the 6-digit code from your email.");
      return;
    }
    setVerifying(true);
    try {
      const res = await axios.post(`${API}/api/food-businesses/${business.slug}/claim/verify-otp`, {
        claim_id: claimId,
        code: code.trim(),
      });
      // The bridge now returns a manage_url pointing at the shared
      // SocialPulse owner dashboard (works across all our platforms).
      setManageUrl(res.data?.manage_url || null);
      setBusinessId(res.data?.business_id || null);
      // Persist a lightweight owner marker so the site can offer a
      // "Manage your listing" shortcut on return visits.
      try {
        if (res.data?.business_id) {
          const key = "fp_owner_businesses";
          const existing = JSON.parse(localStorage.getItem(key) || "[]");
          const next = [...existing.filter((x) => x.business_id !== res.data.business_id), {
            business_id: res.data.business_id,
            business_slug: res.data.business_slug,
            food_slug: business.slug,
            manage_url: res.data.manage_url,
            claimed_at: new Date().toISOString(),
            name: business.name,
          }];
          localStorage.setItem(key, JSON.stringify(next.slice(-20)));
        }
      } catch { /* localStorage may be unavailable */ }
      setStep(res.data?.auto_verified ? "done" : "proof");
      toast.success(res.data?.auto_verified
        ? "Verified! Domain match — your ✓ badge is live."
        : "Verified! Add optional proof to earn the ✓ badge.");
      if (typeof onSubmitted === "function") onSubmitted();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't verify. Please try again.");
    } finally {
      setVerifying(false);
    }
  };

  const uploadProof = async (file, kind) => {
    if (!file || !claimId || !business?.slug) return;
    if (proofs.length >= MAX_PROOFS) {
      toast.error(`Up to ${MAX_PROOFS} files. Remove one to upload another.`);
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File too large — max 5 MB per proof.");
      return;
    }
    setUploadingKind(kind);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("kind", kind);
      fd.append("label", file.name.slice(0, 200));
      const res = await axios.post(
        `${API}/api/food-businesses/${business.slug}/claim/${claimId}/upload-proof`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      setProofs((p) => [...p, res.data.proof]);
      toast.success("Uploaded — reviewer will confirm within 24h.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed. Please retry.");
    } finally {
      setUploadingKind("");
    }
  };

  const removeProof = async (proofId) => {
    try {
      await axios.delete(`${API}/api/food-businesses/${business.slug}/claim/${claimId}/proof/${proofId}`);
      setProofs((p) => p.filter((x) => x.id !== proofId));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't remove file.");
    }
  };

  const resend = async () => {
    if (resending || resendCountdown > 0) return;
    setResending(true);
    try {
      const res = await axios.post(`${API}/api/food-businesses/${business.slug}/claim/resend-otp`, {
        claim_id: claimId,
      });
      setOtpDev(res.data?.otp_dev || null);
      setEmailStatus(res.data?.email_status || null);
      setResendCountdown(45);
      setCode("");
      toast.success(
        res.data?.email_status === "sent"
          ? "New code sent — check your email!"
          : "New code generated."
      );
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't resend code.");
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4"
         onClick={onClose} data-testid="business-claim-modal">
      <div className="bg-white w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl max-h-[92vh] overflow-y-auto"
           onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b px-5 py-3 flex items-center justify-between" style={{ borderColor: "#E9D8B4" }}>
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest opacity-60">Claim business</div>
            <h2 className="font-display text-lg" style={{ color: "#3E2A0A" }}>{business?.name}</h2>
          </div>
          <button onClick={onClose} aria-label="Close" data-testid="business-claim-close"
                  className="w-8 h-8 rounded-full border flex items-center justify-center hover:bg-gray-50"
                  style={{ borderColor: "#E9D8B4" }}>
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={submit} className={`p-5 space-y-3 ${step === "form" ? "" : "hidden"}`}>
          <p className="text-sm opacity-80">
            Tell us who you are — we'll email you a 6-digit code so you can verify ownership and edit hours, menus, photos, and reply to reviews.
          </p>

          <div className="grid grid-cols-2 gap-3">
            <label className="text-xs">
              <span className="font-mono uppercase tracking-wider opacity-70">Your name *</span>
              <input value={form.claimant_name} onChange={(e) => upd("claimant_name", e.target.value)}
                     required maxLength={120} data-testid="business-claim-name"
                     className="mt-1 w-full px-2.5 py-1.5 rounded-lg border text-sm" style={{ borderColor: "#E9D8B4" }} />
            </label>
            <label className="text-xs">
              <span className="font-mono uppercase tracking-wider opacity-70">Your role</span>
              <select value={form.claimant_role} onChange={(e) => upd("claimant_role", e.target.value)}
                      data-testid="business-claim-role"
                      className="mt-1 w-full px-2.5 py-1.5 rounded-lg border text-sm bg-white" style={{ borderColor: "#E9D8B4" }}>
                <option value="owner">Owner</option>
                <option value="manager">Manager</option>
                <option value="employee">Employee</option>
                <option value="other">Other</option>
              </select>
            </label>
          </div>

          <label className="text-xs block">
            <span className="font-mono uppercase tracking-wider opacity-70">Email *</span>
            <input type="email" value={form.claimant_email} onChange={(e) => upd("claimant_email", e.target.value)}
                   required data-testid="business-claim-email"
                   className="mt-1 w-full px-2.5 py-1.5 rounded-lg border text-sm" style={{ borderColor: "#E9D8B4" }} />
            <span className="text-[11px] opacity-60">Preferably at the business domain (e.g. you@yourrestaurant.com).</span>
          </label>

          <label className="text-xs block">
            <span className="font-mono uppercase tracking-wider opacity-70">Phone (optional)</span>
            <input type="tel" value={form.claimant_phone} onChange={(e) => upd("claimant_phone", e.target.value)}
                   data-testid="business-claim-phone"
                   className="mt-1 w-full px-2.5 py-1.5 rounded-lg border text-sm" style={{ borderColor: "#E9D8B4" }} />
          </label>

          <div className="border-t pt-3" style={{ borderColor: "#E9D8B4" }}>
            <div className="text-xs font-mono uppercase tracking-wider opacity-70 mb-2">Fix / add anything wrong?</div>
            <div className="grid grid-cols-2 gap-3">
              <label className="text-xs block">
                <span className="opacity-70">Phone</span>
                <input value={form.correction_phone} onChange={(e) => upd("correction_phone", e.target.value)}
                       data-testid="business-claim-fix-phone"
                       className="mt-1 w-full px-2.5 py-1.5 rounded-lg border text-sm" style={{ borderColor: "#E9D8B4" }} />
              </label>
              <label className="text-xs block">
                <span className="opacity-70">Email</span>
                <input value={form.correction_email} onChange={(e) => upd("correction_email", e.target.value)}
                       data-testid="business-claim-fix-email"
                       className="mt-1 w-full px-2.5 py-1.5 rounded-lg border text-sm" style={{ borderColor: "#E9D8B4" }} />
              </label>
            </div>
            <label className="text-xs block mt-2">
              <span className="opacity-70">Website</span>
              <input value={form.correction_website} onChange={(e) => upd("correction_website", e.target.value)}
                     placeholder="https://…" data-testid="business-claim-fix-website"
                     className="mt-1 w-full px-2.5 py-1.5 rounded-lg border text-sm" style={{ borderColor: "#E9D8B4" }} />
            </label>
            <label className="text-xs block mt-2">
              <span className="opacity-70">Hours</span>
              <input value={form.correction_hours} onChange={(e) => upd("correction_hours", e.target.value)}
                     placeholder="Mon–Fri 11a–10p · Sat–Sun 10a–11p" data-testid="business-claim-fix-hours"
                     className="mt-1 w-full px-2.5 py-1.5 rounded-lg border text-sm" style={{ borderColor: "#E9D8B4" }} />
            </label>
          </div>

          <label className="text-xs block">
            <span className="font-mono uppercase tracking-wider opacity-70">Message (optional)</span>
            <textarea value={form.message} onChange={(e) => upd("message", e.target.value)} rows={3}
                      placeholder="Anything else we should know…"
                      data-testid="business-claim-message" maxLength={2000}
                      className="mt-1 w-full px-2.5 py-1.5 rounded-lg border text-sm" style={{ borderColor: "#E9D8B4" }} />
          </label>

          <div className="pt-2 flex items-center justify-end gap-2 border-t" style={{ borderColor: "#E9D8B4" }}>
            <button type="button" onClick={onClose}
                    className="px-3 py-1.5 rounded-lg text-xs border" style={{ borderColor: "#E9D8B4" }}
                    data-testid="business-claim-cancel">
              Cancel
            </button>
            <button type="submit" disabled={submitting}
                    className="px-4 py-1.5 rounded-lg text-xs font-semibold text-white disabled:opacity-60 inline-flex items-center gap-1.5"
                    style={{ background: "#8B5E10" }}
                    data-testid="business-claim-submit">
              {submitting && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              {submitting ? "Sending code…" : "Send verification code"}
            </button>
          </div>
        </form>

        {/* --------- Step 2: OTP verification --------- */}
        {step === "otp" && (
          <form onSubmit={verify} className="p-5 space-y-4" data-testid="business-claim-otp-panel">
            <div className="text-sm">
              <div className="opacity-80">
                We sent a 6-digit code to
                <b className="ml-1" style={{ color: "#3E2A0A" }}>{form.claimant_email}</b>.
              </div>
              <div className="opacity-60 text-[11px] mt-1">
                Enter it below to finish claiming <b>{business?.name}</b>. Code expires in 15 minutes.
              </div>
              {emailStatus && emailStatus !== "sent" && (
                <div className="mt-2 text-[11px] rounded-md px-2 py-1"
                     style={{ background: "#FEF3C7", color: "#78350F", border: "1px solid #FDE68A" }}
                     data-testid="business-claim-email-warning">
                  Email delivery status: <span className="font-mono">{emailStatus}</span>. If you
                  don't see it, use the dev code below (mock mode).
                </div>
              )}
              {otpDev && (
                <div className="mt-2 text-[11px] rounded-md px-2 py-1 font-mono"
                     style={{ background: "#ECFDF5", color: "#065F46", border: "1px solid #A7F3D0" }}
                     data-testid="business-claim-otp-dev">
                  DEV code: <b>{otpDev}</b>
                </div>
              )}
            </div>

            <label className="text-xs block">
              <span className="font-mono uppercase tracking-wider opacity-70">Verification code</span>
              <input value={code}
                     onChange={(e) => setCode(e.target.value.replace(/\D+/g, "").slice(0, 6))}
                     inputMode="numeric" autoComplete="one-time-code"
                     placeholder="123456" maxLength={6}
                     data-testid="business-claim-otp-input"
                     className="mt-1 w-full px-3 py-2 rounded-lg border text-center font-mono text-2xl tracking-[8px]"
                     style={{ borderColor: "#E9D8B4" }} />
            </label>

            <div className="flex items-center justify-between text-[11px]">
              <button type="button" onClick={() => { setStep("form"); setCode(""); }}
                      className="opacity-70 hover:opacity-100 underline"
                      data-testid="business-claim-otp-back">
                ← Change email
              </button>
              <button type="button" onClick={resend}
                      disabled={resending || resendCountdown > 0}
                      className="opacity-70 hover:opacity-100 underline disabled:opacity-40 disabled:no-underline"
                      data-testid="business-claim-otp-resend">
                {resendCountdown > 0 ? `Resend in ${resendCountdown}s` : (resending ? "Resending…" : "Resend code")}
              </button>
            </div>

            <div className="pt-2 flex items-center justify-end gap-2 border-t" style={{ borderColor: "#E9D8B4" }}>
              <button type="button" onClick={onClose}
                      className="px-3 py-1.5 rounded-lg text-xs border" style={{ borderColor: "#E9D8B4" }}
                      data-testid="business-claim-otp-cancel">
                Cancel
              </button>
              <button type="submit" disabled={verifying || code.length < 4}
                      className="px-4 py-1.5 rounded-lg text-xs font-semibold text-white disabled:opacity-60 inline-flex items-center gap-1.5"
                      style={{ background: "#8B5E10" }}
                      data-testid="business-claim-otp-verify">
                {verifying && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                {verifying ? "Verifying…" : "Verify & claim"}
              </button>
            </div>
          </form>
        )}

        {/* --------- Step 2.5: Proof upload (skippable) --------- */}
        {step === "proof" && (
          <div className="p-5 space-y-4" data-testid="business-claim-proof-panel">
            <div>
              <h3 className="font-display text-lg" style={{ color: "#3E2A0A" }}>
                One last thing — earn your <span style={{ color: "#166534" }}>✓ Verified</span> badge
              </h3>
              <p className="text-xs mt-1 opacity-80">
                Attach any documents that prove you own or run <b>{business?.name}</b>. A moderator
                reviews within 24 hours, then your listing gets the ✓ badge and unlocks
                the 30-day free automated website builder.
              </p>
              <p className="text-[11px] mt-2 opacity-70">
                Accepted: business license · utility bill · letterheaded receipt · chamber-of-commerce
                or business-association letter · storefront/interior photos. JPG · PNG · PDF · up to 5 MB each · 5 files max.
              </p>
            </div>

            {/* Uploaded so far */}
            {proofs.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="business-claim-proof-list">
                {proofs.map((p) => (
                  <div key={p.id}
                       className="relative rounded-lg border overflow-hidden aspect-square"
                       style={{ borderColor: "#E9D8B4", background: "#FDF6E3" }}
                       data-testid={`business-claim-proof-item-${p.id}`}>
                    {p.content_type?.startsWith("image/") ? (
                      <div className="w-full h-full flex items-center justify-center text-[10px] font-mono opacity-60 px-2 text-center">
                        {p.filename || "image"}
                      </div>
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-[10px] font-mono opacity-60 px-2 text-center">
                        PDF · {p.filename || "document"}
                      </div>
                    )}
                    <div className="absolute inset-x-0 bottom-0 bg-white/95 px-1.5 py-1 text-[10px] flex items-center justify-between">
                      <span className="uppercase tracking-wider opacity-70">
                        {(PROOF_KINDS.find((k) => k.key === p.kind)?.label || p.kind).slice(0, 18)}
                      </span>
                      <button onClick={() => removeProof(p.id)}
                              className="opacity-60 hover:opacity-100"
                              data-testid={`business-claim-proof-remove-${p.id}`}
                              type="button">
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Kind buttons */}
            {proofs.length < MAX_PROOFS && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {PROOF_KINDS.map((k) => (
                  <label
                    key={k.key}
                    className="rounded-lg border p-2.5 cursor-pointer hover:shadow-sm block"
                    style={{ borderColor: "#E9D8B4", background: uploadingKind === k.key ? "#FBEED6" : "#fff" }}
                    data-testid={`business-claim-proof-kind-${k.key}`}>
                    <div className="text-xs font-semibold" style={{ color: "#3E2A0A" }}>{k.label}</div>
                    <div className="text-[10px] opacity-60 leading-tight mt-0.5">{k.hint}</div>
                    <input type="file"
                           accept="image/jpeg,image/png,image/webp,image/heic,application/pdf"
                           disabled={!!uploadingKind}
                           onChange={(e) => {
                             const f = e.target.files?.[0];
                             if (f) uploadProof(f, k.key);
                             e.target.value = "";  // allow re-picking same file
                           }}
                           className="hidden" />
                    {uploadingKind === k.key && (
                      <div className="text-[10px] mt-1 flex items-center gap-1" style={{ color: "#8B5E10" }}>
                        <Loader2 className="w-3 h-3 animate-spin" /> uploading…
                      </div>
                    )}
                  </label>
                ))}
              </div>
            )}

            <div className="pt-3 flex items-center justify-between gap-2 border-t"
                 style={{ borderColor: "#E9D8B4" }}>
              <button type="button"
                      onClick={() => setStep("done")}
                      className="text-xs underline opacity-70 hover:opacity-100"
                      data-testid="business-claim-proof-skip">
                Skip for now — I'll add proof later
              </button>
              <button type="button"
                      onClick={() => setStep("done")}
                      disabled={!!uploadingKind}
                      className="px-4 py-1.5 rounded-lg text-xs font-semibold text-white disabled:opacity-60"
                      style={{ background: "#8B5E10" }}
                      data-testid="business-claim-proof-finish">
                {proofs.length > 0 ? "Finish · Continue to dashboard →" : "Continue without proof →"}
              </button>
            </div>
          </div>
        )}

        {/* --------- Step 3: Success --------- */}
        {step === "done" && (
          <div className="p-6 text-center space-y-3" data-testid="business-claim-done-panel">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full"
                 style={{ background: "#DCFCE7", color: "#166534" }}>
              <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" strokeWidth="2.5"
                   strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5"/></svg>
            </div>
            <h3 className="font-display text-lg" style={{ color: "#3E2A0A" }}>You own this listing.</h3>
            <p className="text-sm opacity-80">
              <b>{business?.name}</b> is now yours to manage. You can edit hours, photos,
              menu, and reply to reviews from your owner dashboard — the same account works
              across Food Pulse, Social Pulse and our regional sites.
            </p>
            {proofs.length > 0 && (
              <div className="rounded-lg text-[11px] px-3 py-2 mx-auto max-w-xs"
                   style={{ background: "#ECFDF5", color: "#065F46", border: "1px solid #A7F3D0" }}
                   data-testid="business-claim-proof-summary">
                {proofs.length} proof{proofs.length !== 1 ? "s" : ""} submitted — a moderator
                reviews within 24 hours and you'll get an email when the <b>✓ Verified</b> badge lights up.
              </div>
            )}
            <div className="rounded-lg text-[11px] px-3 py-2 mx-auto max-w-xs"
                 style={{ background: "#FEF9C3", color: "#78350F", border: "1px solid #FDE68A" }}>
              We'll email <span className="font-mono">{form.claimant_email}</span> a magic-link
              to sign in. First time? It's a one-click sign-in — no password.
            </div>
            <div className="flex flex-col sm:flex-row items-stretch gap-2 justify-center pt-1">
              {manageUrl && (
                <Link to={manageUrl}
                      className="px-4 py-2 rounded-lg text-xs font-semibold text-white text-center"
                      style={{ background: "#8B5E10" }}
                      data-testid="business-claim-open-dashboard"
                      onClick={onClose}>
                  Open owner dashboard →
                </Link>
              )}
              <button onClick={onClose}
                      className="px-4 py-2 rounded-lg text-xs font-semibold text-center border"
                      style={{ borderColor: "#E9D8B4", color: "#3E2A0A" }}
                      data-testid="business-claim-done-close">
                Done
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function FoodBusinessPage() {
  const { slug } = useParams();
  const location = useLocation();
  const [biz, setBiz] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [claimOpen, setClaimOpen] = useState(false);

  // Auto-open the claim modal when the caller passed ?claim=1 (used by
  // the City Directory "Claim it" CTA).
  useEffect(() => {
    const p = new URLSearchParams(location.search);
    if (p.get("claim") === "1") setClaimOpen(true);
  }, [location.search]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    (async () => {
      try {
        const res = await axios.get(`${API}/api/food-businesses/${slug}`);
        if (cancelled) return;
        setBiz(res.data);
        document.title = `${res.data.name} — Food Pulse`;
      } catch (e) {
        if (e?.response?.status === 404) setNotFound(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#FBF7F0" }}>
        <div className="text-sm opacity-70" data-testid="business-detail-loading">Loading business profile…</div>
      </div>
    );
  }

  if (notFound || !biz) {
    return (
      <div className="min-h-screen p-8" style={{ background: "#FBF7F0" }}>
        <div className="max-w-3xl mx-auto rounded-2xl border p-10 text-center bg-white"
             style={{ borderColor: "#C79A3F" }} data-testid="business-detail-not-found">
          <Sparkles className="w-6 h-6 mx-auto mb-2" style={{ color: "#8B5E10" }} />
          <div className="font-display text-lg mb-1">Business not found.</div>
          <div className="text-sm opacity-70 mb-4">This listing may have been removed or the URL is incorrect.</div>
          <Link to="/food-pulse/businesses" className="text-sm underline" style={{ color: "#8B5E10" }}>
            ← Back to directory
          </Link>
        </div>
      </div>
    );
  }

  const cover = (biz.images || [])[0] || categoryIllustration(biz.business_type, biz.slug, {
    subType: biz.sub_type,
    cuisines: biz.cuisines,
    name: biz.name,
  });
  const mapsUrl = biz.lat && biz.lon
    ? `https://www.openstreetmap.org/?mlat=${biz.lat}&mlon=${biz.lon}#map=17/${biz.lat}/${biz.lon}`
    : null;

  return (
    <div className="min-h-screen" style={{ background: "#FBF7F0" }} data-testid="business-detail-page">
      <SeoHead
        section="food-pulse"
        title={`${biz.name} — Food Pulse`}
        description={biz.description || `${prettyType(biz.business_type)} in ${biz.city || ""}, ${biz.state_code || "USA"}. Verified business profile on Food Pulse.`}
      />

      {/* Hero */}
      <section className="border-b"
               style={{ borderColor: "#E9D8B4", background: "linear-gradient(180deg,#F0E2C7 0%,#FBF7F0 100%)" }}>
        <div className="max-w-5xl mx-auto px-4 py-8">
          <Link to="/food-pulse/businesses"
                className="inline-flex items-center gap-1 text-xs opacity-70 hover:opacity-100 mb-4"
                data-testid="business-back-link">
            <ArrowLeft className="w-3 h-3" /> All businesses
          </Link>

          <div className="flex flex-col sm:flex-row gap-6 items-start">
            <img src={cover} alt={biz.name}
                 className="w-full sm:w-48 aspect-[4/3] object-cover rounded-2xl border"
                 style={{ borderColor: "#E9D8B4" }} />
            <div className="flex-1">
              <div className="inline-flex items-center gap-2 flex-wrap mb-2">
                <span className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest rounded-full px-2 py-0.5"
                      style={{ background: "#fff", border: "1px solid #C79A3F", color: "#8B5E10" }}>
                  <Utensils className="w-3 h-3" /> {prettyType(biz.business_type)}
                </span>
                {biz.verified && (
                  <span className="inline-flex items-center gap-1 text-[10px] font-mono px-2 py-0.5 rounded-full"
                        style={{ background: "#DCEAD1", color: "#2F6B2F" }} data-testid="business-verified-badge">
                    <BadgeCheck className="w-3 h-3" /> Verified
                  </span>
                )}
                {biz.claim_status && biz.claim_status !== "claimed" && (
                  <span className="inline-flex items-center gap-1 text-[10px] font-mono px-2 py-0.5 rounded-full"
                        style={{ background: "#fff", border: "1px dashed #C79A3F", color: "#8B5E10" }}>
                    Unclaimed
                  </span>
                )}
              </div>
              <h1 className="font-display font-bold leading-tight text-3xl sm:text-4xl lg:text-5xl mb-1"
                  style={{ color: "#3E2A0A" }} data-testid="business-name">
                {biz.name}
              </h1>
              {biz.brand?.name && biz.brand.slug !== biz.slug && (
                <div className="text-sm mb-2 opacity-80">
                  Part of{" "}
                  <Link to={`/food-pulse/brands/${biz.brand.slug}`} className="underline" style={{ color: "#8B5E10" }}>
                    {biz.brand.name}
                  </Link>
                </div>
              )}
              {(biz.city || biz.state) && (
                <div className="inline-flex items-center gap-1 text-sm opacity-80">
                  <MapPin className="w-4 h-4" />
                  {[biz.address, biz.city, biz.state_code, biz.zip].filter(Boolean).join(", ")}
                </div>
              )}

              {biz.cuisines && biz.cuisines.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1">
                  {biz.cuisines.map((c) => (
                    <span key={c} className="text-[11px] font-mono px-2 py-0.5 rounded-full"
                          style={{ background: "#F0E2C7", color: "#8B5E10" }}>
                      {c}
                    </span>
                  ))}
                </div>
              )}

              <div className="mt-4 flex flex-wrap gap-2">
                <button type="button" disabled
                        title="Coming soon — sign in to save this to your list"
                        className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border cursor-not-allowed opacity-70"
                        style={{ background: "#fff", borderColor: "#C79A3F", color: "#8B5E10" }}
                        data-testid="business-like-btn">
                  <Heart className="w-3.5 h-3.5" /> Save {typeof biz.likes_count === "number" && biz.likes_count > 0 ? `· ${biz.likes_count}` : ""}
                </button>
                {(biz.business_type === "farm" || biz.is_farm) && (
                  <Link to={`/food-pulse/farm/${biz.slug}`}
                        title="See the farm's public farmstand page"
                        className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border font-semibold hover:shadow-sm transition-shadow"
                        style={{ background: "#F0F5F1", borderColor: "#0F3D2E", color: "#0F3D2E" }}
                        data-testid="business-farmstand-btn">
                    <Sprout className="w-3.5 h-3.5" /> View Farmstand
                  </Link>
                )}
                <button type="button" onClick={() => setClaimOpen(true)}
                        title="Verify you own this business and enhance the listing"
                        className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border font-semibold hover:shadow-sm transition-shadow"
                        style={{ background: "#8B5E10", borderColor: "#8B5E10", color: "#fff" }}
                        data-testid="business-claim-btn">
                  <BadgeCheck className="w-3.5 h-3.5" /> Claim this listing
                </button>
                <ShareButton
                  title={biz.name}
                  label={`Food Pulse — ${biz.name}`}
                  testId="business-share-btn"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* "Is this your business?" claim invitation banner (hides once claimed) */}
      {biz.claim_status !== "claimed" && (
        <section className="max-w-5xl mx-auto px-4 pt-4">
          <div className="rounded-2xl border-2 border-dashed p-4 sm:p-5 flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4"
               style={{ borderColor: "#C79A3F", background: "linear-gradient(90deg,#FFF5DA 0%,#FBF7F0 100%)" }}
               data-testid="business-claim-banner">
            <BadgeCheck className="w-8 h-8 flex-none" style={{ color: "#8B5E10" }} />
            <div className="flex-1">
              <div className="font-display text-base sm:text-lg" style={{ color: "#3E2A0A" }}>
                Is this Your Business? Claim it and Enhance it!
              </div>
              <div className="text-xs sm:text-sm opacity-80 mt-0.5">
                Add photos, menus, hours, and reply to reviews. Free — takes 2 minutes.
              </div>
            </div>
            <button type="button" onClick={() => setClaimOpen(true)}
                    className="inline-flex items-center gap-1.5 text-xs sm:text-sm px-4 py-2 rounded-full font-semibold text-white hover:shadow-md transition-shadow flex-none"
                    style={{ background: "#8B5E10" }}
                    data-testid="business-claim-banner-btn">
              Claim it →
            </button>
          </div>
        </section>
      )}

      {/* Body */}
      <section className="max-w-5xl mx-auto px-4 py-8 grid gap-6 md:grid-cols-3">
        {/* Left — description + amenities + AI-enriched sections */}
        <div className="md:col-span-2 space-y-6">
          <CharityImpactBadges biz={biz} />

          {(biz.long_description || biz.description) && (
            <div className="rounded-2xl border bg-white p-5" style={{ borderColor: "#E9D8B4" }} data-testid="business-description">
              <div className="font-display text-lg mb-2" style={{ color: "#3E2A0A" }}>About</div>
              <p className="text-sm opacity-90 whitespace-pre-line">{biz.long_description || biz.description}</p>
              {biz.established_year && (
                <div className="mt-3 text-xs opacity-70">Established {biz.established_year}{biz.owner_names?.length ? ` · Owners: ${biz.owner_names.join(", ")}` : ""}.</div>
              )}
            </div>
          )}

          {biz.signature_items?.length > 0 && (
            <div className="rounded-2xl border bg-white p-5" style={{ borderColor: "#E9D8B4" }} data-testid="business-signature-items">
              <div className="font-display text-lg mb-3" style={{ color: "#3E2A0A" }}>Signature items</div>
              <ul className="space-y-2 text-sm">
                {biz.signature_items.map((it, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <Sparkles className="w-3.5 h-3.5 mt-0.5 flex-none" style={{ color: "#8B5E10" }} />
                    <span>{typeof it === "string" ? it : it?.name || JSON.stringify(it)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {(biz.amenities || biz.dietary_tags) && (
            <div className="rounded-2xl border bg-white p-5" style={{ borderColor: "#E9D8B4" }} data-testid="business-amenities">
              <div className="font-display text-lg mb-2" style={{ color: "#3E2A0A" }}>Amenities & options</div>
              <AmenityRow amenities={biz.amenities} />
              {biz.dietary_tags && biz.dietary_tags.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2" data-testid="business-dietary-tags">
                  {biz.dietary_tags.map((t) => (
                    <span key={t} className="text-[11px] font-mono px-2 py-0.5 rounded-full"
                          style={{ background: "#DCEAD1", color: "#2F6B2F" }}>
                      {t.replace(/_/g, " ")}
                    </span>
                  ))}
                </div>
              )}
              {biz.payment_methods?.length > 0 && (
                <div className="mt-3">
                  <div className="text-[10px] font-mono uppercase tracking-wider opacity-60 mb-1">Payments</div>
                  <div className="flex flex-wrap gap-1">
                    {biz.payment_methods.map((p) => (
                      <span key={p} className="text-[11px] font-mono px-2 py-0.5 rounded-full inline-flex items-center gap-1"
                            style={{ background: "#F0E2C7", color: "#8B5E10" }}>
                        <DollarSign className="w-3 h-3" /> {p}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {biz.parking_info && (
                <div className="mt-3 text-xs opacity-80"><span className="font-semibold">Parking:</span> {biz.parking_info}</div>
              )}
            </div>
          )}

          {biz.photos_ai?.length > 0 && (
            <div className="rounded-2xl border bg-white p-5" style={{ borderColor: "#E9D8B4" }} data-testid="business-photos-ai">
              <div className="font-display text-lg mb-3" style={{ color: "#3E2A0A" }}>Photos</div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {biz.photos_ai.slice(0, 6).map((p, i) => (
                  <a key={i} href={p.url} target="_blank" rel="noreferrer"
                     className="group block aspect-square rounded-lg overflow-hidden border" style={{ borderColor: "#E9D8B4" }}>
                    <img src={p.url} alt={p.caption || biz.name}
                         className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                  </a>
                ))}
              </div>
              <div className="text-[10px] opacity-50 mt-2">Sourced from public listings. Credits maintained per image.</div>
            </div>
          )}

          {biz.review_summary?.avg_rating && (
            <div className="rounded-2xl border bg-white p-5" style={{ borderColor: "#E9D8B4" }} data-testid="business-review-summary">
              <div className="flex items-center justify-between mb-2">
                <div className="font-display text-lg" style={{ color: "#3E2A0A" }}>Reviews</div>
                <div className="flex items-center gap-1">
                  <Star className="w-4 h-4 fill-current" style={{ color: "#EAB308" }} />
                  <span className="font-bold">{biz.review_summary.avg_rating}</span>
                  {biz.review_summary.review_count && (
                    <span className="text-xs opacity-70">· {biz.review_summary.review_count} reviews</span>
                  )}
                </div>
              </div>
              {biz.review_summary.top_positive && (
                <blockquote className="text-sm italic opacity-90 border-l-2 pl-3 mb-2" style={{ borderColor: "#DCEAD1" }}>
                  "{biz.review_summary.top_positive}"
                </blockquote>
              )}
              {biz.review_summary.top_negative && (
                <blockquote className="text-sm italic opacity-70 border-l-2 pl-3" style={{ borderColor: "#F0C0C0" }}>
                  "{biz.review_summary.top_negative}"
                </blockquote>
              )}
              {biz.review_summary.sources?.length > 0 && (
                <div className="text-[10px] opacity-60 mt-2">Aggregated from {biz.review_summary.sources.join(", ")}.</div>
              )}
            </div>
          )}

          {biz.press_mentions?.length > 0 && (
            <div className="rounded-2xl border bg-white p-5" style={{ borderColor: "#E9D8B4" }} data-testid="business-press">
              <div className="font-display text-lg mb-2 inline-flex items-center gap-2" style={{ color: "#3E2A0A" }}>
                <Newspaper className="w-4 h-4" /> Press mentions
              </div>
              <ul className="space-y-1.5 text-sm">
                {biz.press_mentions.slice(0, 8).map((p, i) => (
                  <li key={i}>
                    {p.url ? <a href={p.url} target="_blank" rel="noreferrer" className="underline hover:opacity-80" style={{ color: "#8B5E10" }}>{p.title || p.publisher}</a> : <span>{p.title || p.publisher}</span>}
                    {p.publisher && p.title && <span className="opacity-60"> · {p.publisher}</span>}
                    {p.year && <span className="opacity-60"> · {p.year}</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {biz.awards?.length > 0 && (
            <div className="rounded-2xl border bg-white p-5" style={{ borderColor: "#E9D8B4" }} data-testid="business-awards">
              <div className="font-display text-lg mb-2 inline-flex items-center gap-2" style={{ color: "#3E2A0A" }}>
                <Award className="w-4 h-4" /> Awards
              </div>
              <ul className="space-y-1.5 text-sm">
                {biz.awards.map((a, i) => (
                  <li key={i}>
                    <span className="font-semibold">{a.name}</span>
                    {a.year && <span className="opacity-60"> · {a.year}</span>}
                    {a.awarder && <span className="opacity-60"> · {a.awarder}</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {biz.faqs?.length > 0 && (
            <div className="rounded-2xl border bg-white p-5" style={{ borderColor: "#E9D8B4" }} data-testid="business-faqs">
              <div className="font-display text-lg mb-3" style={{ color: "#3E2A0A" }}>FAQs</div>
              <div className="space-y-3">
                {biz.faqs.map((f, i) => (
                  <div key={i}>
                    <div className="text-sm font-semibold">{f.q}</div>
                    <div className="text-sm opacity-90 mt-0.5">{f.a}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <CharityContactForm biz={biz} />
        </div>

        {/* Right — contact + hours + social */}
        <aside className="space-y-6">
          <SplinkQrCard
            longUrl={typeof window !== "undefined" ? window.location.origin + window.location.pathname : ""}
            label={biz.name}
            site="foodpulse"
            title="Share this business"
            subtitle="Scan to open on any phone · print on menus & table cards."
          />
          <div className="text-center">
            <Link
              to={`/food-pulse/businesses/${biz.slug}/table-card`}
              className="inline-flex items-center gap-1 text-xs font-semibold underline"
              style={{ color: "#7C3AED" }}
              data-testid="table-card-link"
            >
              Print a table card / take-out label →
            </Link>
          </div>
          <div className="rounded-2xl border bg-white p-5" style={{ borderColor: "#E9D8B4" }} data-testid="business-contact">
            <div className="font-display text-lg mb-3" style={{ color: "#3E2A0A" }}>Contact</div>
            <div className="space-y-2 text-sm">
              {biz.phone && (
                <a href={`tel:${biz.phone}`} className="flex items-center gap-2 hover:underline"
                   data-testid="business-phone" style={{ color: "#3E2A0A" }}>
                  <Phone className="w-4 h-4 opacity-60" /> {biz.phone}
                </a>
              )}
              {biz.website && (
                <a href={biz.website} target="_blank" rel="noreferrer"
                   className="flex items-center gap-2 hover:underline break-all" data-testid="business-website"
                   style={{ color: "#3E2A0A" }}>
                  <Globe className="w-4 h-4 opacity-60" />
                  <span className="truncate">{biz.website.replace(/^https?:\/\//, "")}</span>
                  <ExternalLink className="w-3 h-3 opacity-60 shrink-0" />
                </a>
              )}
              {biz.email && (
                <a href={`mailto:${biz.email}`} className="flex items-center gap-2 hover:underline"
                   data-testid="business-email" style={{ color: "#3E2A0A" }}>
                  <Mail className="w-4 h-4 opacity-60" /> {biz.email}
                </a>
              )}
              {biz.reservations_url && (
                <a href={biz.reservations_url} target="_blank" rel="noreferrer"
                   className="flex items-center gap-2 hover:underline" data-testid="business-reservations"
                   style={{ color: "#3E2A0A" }}>
                  <BadgeCheck className="w-4 h-4 opacity-60" /> Reservations <ExternalLink className="w-3 h-3 opacity-60" />
                </a>
              )}
              {biz.menu_pdf_url && (
                <a href={biz.menu_pdf_url} target="_blank" rel="noreferrer"
                   className="flex items-center gap-2 hover:underline" data-testid="business-menu"
                   style={{ color: "#3E2A0A" }}>
                  <Utensils className="w-4 h-4 opacity-60" /> View menu <ExternalLink className="w-3 h-3 opacity-60" />
                </a>
              )}
              {mapsUrl && (
                <a href={mapsUrl} target="_blank" rel="noreferrer"
                   className="flex items-center gap-2 hover:underline" data-testid="business-map-link"
                   style={{ color: "#3E2A0A" }}>
                  <MapPin className="w-4 h-4 opacity-60" /> Open in map
                  <ExternalLink className="w-3 h-3 opacity-60" />
                </a>
              )}
              {!biz.phone && !biz.website && !biz.email && (
                <div className="text-xs opacity-60">No contact info published yet.</div>
              )}
            </div>
          </div>

          {biz.social_media && Object.values(biz.social_media).some(Boolean) && (
            <div className="rounded-2xl border bg-white p-5" style={{ borderColor: "#E9D8B4" }} data-testid="business-socials">
              <div className="font-display text-lg mb-3" style={{ color: "#3E2A0A" }}>Follow</div>
              <div className="flex flex-wrap gap-2">
                {biz.social_media.instagram && (
                  <a href={biz.social_media.instagram} target="_blank" rel="noreferrer"
                     className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border text-xs hover:bg-gray-50"
                     style={{ borderColor: "#E9D8B4" }} data-testid="business-social-instagram">
                    <Instagram className="w-3.5 h-3.5" /> Instagram
                  </a>
                )}
                {biz.social_media.facebook && (
                  <a href={biz.social_media.facebook} target="_blank" rel="noreferrer"
                     className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border text-xs hover:bg-gray-50"
                     style={{ borderColor: "#E9D8B4" }} data-testid="business-social-facebook">
                    <Facebook className="w-3.5 h-3.5" /> Facebook
                  </a>
                )}
                {biz.social_media.youtube && (
                  <a href={biz.social_media.youtube} target="_blank" rel="noreferrer"
                     className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border text-xs hover:bg-gray-50"
                     style={{ borderColor: "#E9D8B4" }} data-testid="business-social-youtube">
                    <Youtube className="w-3.5 h-3.5" /> YouTube
                  </a>
                )}
                {biz.social_media.tiktok && (
                  <a href={biz.social_media.tiktok} target="_blank" rel="noreferrer"
                     className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border text-xs hover:bg-gray-50"
                     style={{ borderColor: "#E9D8B4" }} data-testid="business-social-tiktok">
                    TikTok
                  </a>
                )}
                {biz.social_media.x && (
                  <a href={biz.social_media.x} target="_blank" rel="noreferrer"
                     className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border text-xs hover:bg-gray-50"
                     style={{ borderColor: "#E9D8B4" }} data-testid="business-social-x">
                    X (Twitter)
                  </a>
                )}
                {biz.social_media.linkedin && (
                  <a href={biz.social_media.linkedin} target="_blank" rel="noreferrer"
                     className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border text-xs hover:bg-gray-50"
                     style={{ borderColor: "#E9D8B4" }} data-testid="business-social-linkedin">
                    LinkedIn
                  </a>
                )}
              </div>
            </div>
          )}

          {biz.hours && (
            <div className="rounded-2xl border bg-white p-5" style={{ borderColor: "#E9D8B4" }} data-testid="business-hours">
              <div className="font-display text-lg mb-2 inline-flex items-center gap-2" style={{ color: "#3E2A0A" }}>
                <Clock className="w-4 h-4" /> Hours
              </div>
              <div className="text-xs whitespace-pre-line opacity-90 font-mono">{biz.hours}</div>
            </div>
          )}

          {biz.delivery_partners?.length > 0 && (
            <div className="rounded-2xl border bg-white p-5" style={{ borderColor: "#E9D8B4" }} data-testid="business-delivery">
              <div className="font-display text-lg mb-2" style={{ color: "#3E2A0A" }}>Order delivery</div>
              <div className="flex flex-wrap gap-1.5">
                {biz.delivery_partners.map((p) => (
                  <span key={p} className="text-[11px] font-mono px-2 py-0.5 rounded-full"
                        style={{ background: "#F0E2C7", color: "#8B5E10" }}>{p}</span>
                ))}
              </div>
            </div>
          )}

          {biz.nearby_landmarks?.length > 0 && (
            <div className="rounded-2xl border bg-white p-5" style={{ borderColor: "#E9D8B4" }} data-testid="business-landmarks">
              <div className="text-[10px] font-mono uppercase tracking-wider opacity-60 mb-1">Nearby</div>
              <ul className="text-xs space-y-0.5">
                {biz.nearby_landmarks.map((l, i) => (
                  <li key={i} className="opacity-80">• {l}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="rounded-2xl border p-5 text-[11px] opacity-70"
               style={{ borderColor: "#E9D8B4", background: "#FBF7F0" }}>
            Data sourced from {biz.source === "osm" ? "OpenStreetMap contributors (ODbL)" :
                               biz.source === "usda" ? "USDA Local Food Portal" :
                               biz.source === "feeding_america" ? "Feeding America" :
                               "verified public sources"}.
            {biz.master_generated_at && <> AI-enriched {new Date(biz.master_generated_at).toLocaleDateString()}.</>}
            {biz.updated_at && <> Last updated {new Date(biz.updated_at).toLocaleDateString()}.</>}
          </div>
        </aside>
      </section>

      <section className="max-w-6xl mx-auto px-4 pb-16">
        <TalkVibesTab
          entityType="food_business"
          entityId={biz.slug}
          subject={biz.name}
          intro={`Been to ${biz.name}? Drop a photo, share a deal you spotted, ask about parking or hours, or flag a rescue-ready end-of-day discount for other shoppers.`}
        />
      </section>

      {claimOpen && <ClaimModal business={biz} onClose={() => setClaimOpen(false)} onSubmitted={() => setBiz({ ...biz, claim_status: "pending" })} />}
    </div>
  );
}
